/**
 * Purpose: Utility functions that are used by the selectors to return their results, but are not true selectors themselves.
 *
 * Note: There are functions here that work directly with state they do not follow normal selector patterns and they're not used
 *   directly by the applications, but serve the needs of the selectors instead.
 */

// interfaces/types
import { KEYID_DELIMETER } from '../types/commonTypes';
import { parseIncentiveId, resolveIncentiveTermRange } from '../utils/incentives';

const TERM_SUFFIX = ' mo.';
const APR_SUFFIX = 'APR';
import numeral from 'numeral';
import type { BirthDate } from '@makemydeal/dr-common-utils';
import type { CreditTierByIdDetails, GenericObject, LeaseAnnualMiles, Offer, PaymentTerm } from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';

import { OFFER_STATE_SIGNATURE } from '@makemydeal/dr-platform-types';

export const getStateSlice = (state: OfferReduxBaseState, sliceKey: any, slice: any) => {
    return (state?.[sliceKey] ? state : state?.[slice]) ?? {};
};

/**
 * This may seem like a "selector", but it is really a utility that works with state to retrieve the base state where offer
 * resides in the state tree.
 */
export const getOfferNode = (state: OfferReduxBaseState): Offer => {
    const offer = getStateSlice(state, OFFER_STATE_SIGNATURE, 'offer');
    return offer;
};

export interface IObjectWithIDAndName {
    id: string;
    name: string;
}

export const getMileageOptions = (annualMilesOptions?: LeaseAnnualMiles) => {
    if (!annualMilesOptions || Object.keys(annualMilesOptions).length === 0) {
        return [];
    }

    const mileageOptions: OfferReduxBaseState[] = [];
    Object.keys(annualMilesOptions).forEach((key) => {
        const mileage = {} as OfferReduxBaseState;
        const option = annualMilesOptions[Number(key)];

        mileage.id = option;
        mileage.name = `${numeral(option).format('0,0')} Miles`;
        mileageOptions.push(mileage);
    });

    return mileageOptions;
};

export const getCreditTierRanges = (creditTiers?: any[]) => {
    if (!creditTiers || creditTiers.length === 0) {
        return [];
    }

    const creditLevels: CreditTierByIdDetails[] = [];
    creditTiers.forEach((tier) => {
        const tierLevel = {} as CreditTierByIdDetails;

        tierLevel.id = tier.id;
        tierLevel.name = tier.label;
        creditLevels.push(tierLevel);
    });

    return creditLevels;
};

export interface IFinanceOrLeaseTermsFormattedOptions {
    id: string;
    name: string;
    display?: string;
}

export const formatOptionsForFinanceTerms = (term: PaymentTerm): IFinanceOrLeaseTermsFormattedOptions => {
    return {
        id: term.id,
        name: `${term.months} ${TERM_SUFFIX} at ${term.apr}% ${APR_SUFFIX}`,
        display: `${term.months} ${TERM_SUFFIX}`
    };
};

export const formatOptionsForLeaseTerms = (term: PaymentTerm): IFinanceOrLeaseTermsFormattedOptions => {
    return {
        id: term.id,
        name: `${term.months} ${TERM_SUFFIX}`
    };
};

export const getMappedIncentiveId = (mappingIncentives: GenericObject<number>, incentiveId: any) => {
    return mappingIncentives?.[incentiveId] || {};
};

export const getMappedIncentives = (mappingIncentives: GenericObject<number>, incentives: string[] = []) => {
    const mappedIncentives: number[] = [];
    incentives.forEach((incentive) => {
        // eslint-disable-next-line no-prototype-builtins
        if (mappingIncentives.hasOwnProperty(incentive)) {
            mappedIncentives.push(mappingIncentives[incentive]);
            return;
        }
        const parsedId = parseIncentiveId(String(incentive));
        if (!Number.isNaN(parsedId)) {
            mappedIncentives.push(parsedId);
        }
    });
    return mappedIncentives;
};

export const findIncentiveTotal = (incentives: any): number => {
    if (!incentives || incentives.length === 0) {
        return 0;
    }

    let total = 0;
    incentives.forEach((incentive: any) => {
        total += incentive.amount;
    });

    return total;
};

export const getIncentivesWithAmount = (incentives: any[]) => {
    if (!incentives || incentives.length === 0) {
        return [];
    }
    return incentives.filter((incentive) => incentive?.amount > 0);
};

export const transformIncentiveAmount = (termsByMonth: number, incentive: any): number => {
    if (incentive?.amount) {
        return incentive.amount;
    }
    let result = incentive?.amount || 0;

    // this just makes sure that if this function is used with different shapes of the incentive, we get the right data
    const termRangeList = resolveIncentiveTermRange(incentive) || [];

    termRangeList.forEach((termRange: any) => {
        if (termsByMonth >= termRange.startTerm && termsByMonth <= termRange.endTerm) {
            result = termRange.customerCash;
        }
    });

    return result;
};

export const sanitizeTermId = (termId: string) => {
    return termId?.replace(/\./g, KEYID_DELIMETER);
};

export function isBirthMonthValid(birthDate: BirthDate | undefined) {
    if (!birthDate) return false;
    return Number(birthDate.birthMonth) <= 12 && Number(birthDate.birthMonth) > 0;
}

export const getSliceData = (
    state: any,
    fields: string[],
    filter: boolean = false,
    slice: OfferReduxBaseState = {}
): OfferReduxBaseState => {
    fields
        .filter((key) => (filter ? state[key] : true))
        .forEach((key) => {
            slice[key] = state[key];
        });

    return slice;
};

export const hasUndefinedValue = (values: any[]): boolean => {
    return values?.some((value) => value == null);
};

export const objectHasProperty = (object: any, prop: string): boolean => {
    return Object.prototype.hasOwnProperty.call(object, prop) && object[prop] !== null;
};
